import { useEffect } from 'react';
import Chart from 'chart.js/auto';
import { Doughnut, Line, Pie, Bar } from 'react-chartjs-2';
import { getAdminProducts } from '../../actions/productAction';
import { useSelector, useDispatch } from 'react-redux';
import { getAllOrders } from '../../actions/orderAction';
import { getAllUsers } from '../../actions/userAction';
import { categories } from '../../utils/constants';
import MetaData from '../Layouts/MetaData';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

const MainData = () => {
    const dispatch = useDispatch();
    const theme = useTheme();

    const { products } = useSelector((state) => state.products);
    const { orders } = useSelector((state) => state.allOrders);
    const { users } = useSelector((state) => state.users);

    let outOfStock = 0;
    products?.forEach((item) => {
        if (item.stock === 0) {
            outOfStock += 1;
        }
    });

    useEffect(() => {
        dispatch(getAdminProducts());
        dispatch(getAllOrders());
        dispatch(getAllUsers());
    }, [dispatch]);

    let totalAmount = orders?.reduce((total, order) => total + order.totalPrice, 0);

    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const date = new Date();

    const lineState = {
        labels: months,
        datasets: [
            {
                label: `Sales in ${date.getFullYear() - 2}`,
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.main,
                data: months.map((m, i) => orders?.filter((od) => new Date(od.createdAt).getMonth() === i && new Date(od.createdAt).getFullYear() === date.getFullYear() - 2).reduce((total, od) => total + od.totalPrice, 0)),
            },
            {
                label: `Sales in ${date.getFullYear() - 1}`,
                borderColor: theme.palette.secondary.main,
                backgroundColor: theme.palette.secondary.main,
                data: months.map((m, i) => orders?.filter((od) => new Date(od.createdAt).getMonth() === i && new Date(od.createdAt).getFullYear() === date.getFullYear() - 1).reduce((total, od) => total + od.totalPrice, 0)),
            },
            {
                label: `Sales in ${date.getFullYear()}`,
                borderColor: theme.palette.success.main,
                backgroundColor: theme.palette.success.main,
                data: months.map((m, i) => orders?.filter((od) => new Date(od.createdAt).getMonth() === i && new Date(od.createdAt).getFullYear() === date.getFullYear()).reduce((total, od) => total + od.totalPrice, 0)),
            },
        ],
    };

    const statuses = ['Processing', 'Shipped', 'Delivered'];

    const pieState = {
        labels: statuses,
        datasets: [
            {
                backgroundColor: ['#9333ea', '#facc15', '#4ade80'],
                hoverBackgroundColor: ['#a855f7', '#fde047', '#86efac'],
                data: statuses.map((status) => orders?.filter((item) => item.orderStatus === status).length),
            },
        ],
    };

    const doughnutState = {
        labels: ['Out of Stock', 'In Stock'],
        datasets: [
            {
                backgroundColor: ['#ef4444', '#22c55e'],
                hoverBackgroundColor: ['#dc2626', '#16a34a'],
                data: [outOfStock, products.length - outOfStock],
            },
        ],
    };

    const barState = {
        labels: categories,
        datasets: [
            {
                label: "Products",
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.main,
                hoverBackgroundColor: theme.palette.primary.dark,
                data: categories.map((cat) => products?.filter((item) => item.category === cat).length),
            },
        ],
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
    };

    return (
        <>
            <MetaData title="Admin Dashboard - AmritLok" />

            <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
                initial="hidden"
                animate="visible"
                variants={{
                    visible: {
                        transition: {
                            staggerChildren: 0.1,
                        },
                    },
                }}
            >
                <motion.div className="flex flex-col bg-gradient-to-r from-purple-500 to-purple-600 text-white gap-2 rounded-xl shadow-lg hover:shadow-2xl p-6 transform hover:-translate-y-1 transition-all duration-300" variants={cardVariants}>
                    <h4 className="text-gray-100 font-medium">Total Sales Amount</h4>
                    <h2 className="text-3xl font-bold">₹{totalAmount?.toLocaleString()}</h2>
                </motion.div>
                <motion.div className="flex flex-col bg-gradient-to-r from-red-500 to-red-600 text-white gap-2 rounded-xl shadow-lg hover:shadow-2xl p-6 transform hover:-translate-y-1 transition-all duration-300" variants={cardVariants}>
                    <h4 className="text-gray-100 font-medium">Total Orders</h4>
                    <h2 className="text-3xl font-bold">{orders?.length}</h2>
                </motion.div>
                <motion.div className="flex flex-col bg-gradient-to-r from-yellow-500 to-yellow-600 text-white gap-2 rounded-xl shadow-lg hover:shadow-2xl p-6 transform hover:-translate-y-1 transition-all duration-300" variants={cardVariants}>
                    <h4 className="text-gray-100 font-medium">Total Products</h4>
                    <h2 className="text-3xl font-bold">{products?.length}</h2>
                </motion.div>
                <motion.div className="flex flex-col bg-gradient-to-r from-green-500 to-green-600 text-white gap-2 rounded-xl shadow-lg hover:shadow-2xl p-6 transform hover:-translate-y-1 transition-all duration-300" variants={cardVariants}>
                    <h4 className="text-gray-100 font-medium">Total Users</h4>
                    <h2 className="text-3xl font-bold">{users?.length}</h2>
                </motion.div>
            </motion.div>

            <div className="flex flex-col lg:flex-row justify-between gap-4 sm:gap-8 min-w-full mt-6">
                <motion.div className="bg-white dark:bg-gray-800 rounded-xl h-auto w-full shadow-lg p-4" initial={{ opacity: 0, x: -100 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
                    <Line data={lineState} />
                </motion.div>

                <motion.div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 text-center" initial={{ opacity: 0, x: 100 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.4 }}>
                    <span className="font-medium uppercase text-gray-800 dark:text-gray-200">Order Status</span>
                    <Pie data={pieState} />
                </motion.div>
            </div>

            <div className="flex flex-col lg:flex-row justify-between gap-4 sm:gap-8 min-w-full my-6">
                <motion.div className="bg-white dark:bg-gray-800 rounded-xl h-auto w-full shadow-lg p-4" initial={{ opacity: 0, x: -100 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.6 }}>
                    <Bar data={barState} />
                </motion.div>

                <motion.div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 text-center" initial={{ opacity: 0, x: 100 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.8 }}>
                    <span className="font-medium uppercase text-gray-800 dark:text-gray-200">Stock Status</span>
                    <Doughnut data={doughnutState} />
                </motion.div>
            </div>
        </>
    );
};

export default MainData;